import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Phone, Globe, Gift, ShieldCheck, ArrowLeft, Share2, Copy } from "lucide-react";
import { getDiscount } from "@/lib/api";
import { CATEGORY_ICONS, CATEGORY_LABELS } from "@/lib/categories";
import MapView from "@/components/MapView";
import { toast } from "sonner";

export default function DiscountDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getDiscount(id).then(setD).catch(() => setD(null)).finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="max-w-4xl mx-auto p-8 mono-label text-volt">Loading intel...</div>;
    }
    if (!d) {
        return (
            <div className="max-w-4xl mx-auto p-8 text-center">
                <div className="font-oswald text-2xl uppercase mb-4">Intel not found</div>
                <button data-testid="back-home-btn" onClick={() => navigate("/browse")} className="btn-outline-volt">
                    <ArrowLeft className="w-4 h-4" /> Back to Directory
                </button>
            </div>
        );
    }

    const Icon = CATEGORY_ICONS[d.category] || ShieldCheck;

    const share = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({ title: d.title, text: d.description, url });
            } catch { /* cancelled */ }
        } else {
            navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard");
        }
    };

    const copyDetails = () => {
        navigator.clipboard.writeText(d.discount_details);
        toast.success("Discount details copied");
    };

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
            <Link
                to="/browse"
                data-testid="back-to-browse-link"
                className="mono-label text-[#94a3b8] hover:text-volt inline-flex items-center gap-1 mb-6"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> back to directory
            </Link>

            <div className="grid md:grid-cols-12 gap-8">
                <div className="md:col-span-7">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <div className="inline-flex items-center gap-1 px-2 py-1 border border-white/15">
                            <Icon className="w-3.5 h-3.5 text-volt" />
                            <span className="mono-label text-[10px]">
                                {CATEGORY_LABELS[d.category] || d.category}
                            </span>
                        </div>
                        {d.is_freebie && (
                            <div className="inline-flex items-center gap-1 px-2 py-1 bg-volt/10 border border-volt/40">
                                <Gift className="w-3.5 h-3.5 text-volt" />
                                <span className="mono-label text-volt text-[10px]">FREE</span>
                            </div>
                        )}
                        {d.verification_required && (
                            <div className="inline-flex items-center gap-1 px-2 py-1 border border-white/15">
                                <ShieldCheck className="w-3.5 h-3.5 text-[#00f0ff]" />
                                <span className="mono-label text-[10px]">ID REQUIRED</span>
                            </div>
                        )}
                    </div>

                    <div className="mono-label mb-2">{d.business_name}</div>
                    <h1
                        data-testid="discount-title"
                        className="font-oswald uppercase tracking-wide text-3xl md:text-5xl leading-tight mb-4"
                    >
                        {d.title}
                    </h1>
                    <p className="text-[#94a3b8] text-lg leading-relaxed mb-8">{d.description}</p>

                    <div className="tactical-card p-6 hud-brackets mb-6">
                        <div className="mono-label text-volt mb-2">// THE PERK</div>
                        <p className="text-xl text-white leading-snug mb-4">{d.discount_details}</p>
                        <div className="flex gap-2">
                            <button
                                data-testid="copy-details-btn"
                                onClick={copyDetails}
                                className="btn-outline-volt text-xs"
                            >
                                <Copy className="w-3.5 h-3.5" /> Copy Details
                            </button>
                            <button
                                data-testid="share-btn"
                                onClick={share}
                                className="btn-outline-volt text-xs"
                            >
                                <Share2 className="w-3.5 h-3.5" /> Share
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="tactical-card p-4">
                            <div className="mono-label mb-2">Location</div>
                            <div className="text-sm flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-volt mt-0.5" />
                                <div>
                                    {d.address && <div>{d.address}</div>}
                                    <div>{d.city}, {d.state} {d.zip_code}</div>
                                </div>
                            </div>
                        </div>
                        {d.phone && (
                            <div className="tactical-card p-4">
                                <div className="mono-label mb-2">Comms</div>
                                <a href={`tel:${d.phone}`} className="text-sm flex items-center gap-2 hover:text-volt">
                                    <Phone className="w-4 h-4 text-volt" /> {d.phone}
                                </a>
                            </div>
                        )}
                        {d.website && (
                            <div className="tactical-card p-4 sm:col-span-2">
                                <div className="mono-label mb-2">Website</div>
                                <a
                                    href={d.website}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="website-link"
                                    className="text-sm flex items-center gap-2 hover:text-volt break-all"
                                >
                                    <Globe className="w-4 h-4 text-volt" /> {d.website}
                                </a>
                            </div>
                        )}
                    </div>

                    {d.branches && d.branches.length > 0 && (
                        <div className="mt-6">
                            <div className="mono-label mb-2">Eligible Branches</div>
                            <div className="flex flex-wrap gap-2">
                                {d.branches.map((b) => (
                                    <span key={b} className="mono-label px-2 py-1 border border-white/15 text-[10px]">
                                        {b}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="md:col-span-5">
                    <div className="sticky top-24">
                        <div className="mono-label mb-2">// TARGET COORDINATES</div>
                        <div className="h-[380px]">
                            {d.latitude != null && d.longitude != null ? (
                                <MapView
                                    discounts={[d]}
                                    activeId={d.id}
                                    center={[d.latitude, d.longitude]}
                                />
                            ) : (
                                <div className="tactical-card h-full flex items-center justify-center text-[#64748b] text-sm">
                                    No coordinates on file
                                </div>
                            )}
                        </div>
                        <div className="mono-label text-[10px] text-[#64748b] mt-3">
                            Verify current terms with the business before use.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
